'use client';

/**
 * useUserIdentity
 *
 * 사용자 로그인 시 analytics 서비스들에 사용자 식별 및 속성 설정
 *
 * 🎯 Mixpanel Simplified ID Merge:
 * - 현대 Mixpanel SDK는 identify()만 호출하면 자동으로 ID merge 처리
 * - 별도 alias() 호출 불필요 (레거시 패턴)
 * - https://docs.mixpanel.com/docs/tracking-methods/id-management/identifying-users
 *
 * 🎯 Email 우선 정책:
 * - email이 있으면 email을 distinct_id로 사용
 * - email이 없으면 user.id 사용
 */

import type { User } from '@supabase/supabase-js';
import { useEffect, useRef } from 'react';
import { Mixpanel } from './mixpanel';
import { getAnalyticsTracker } from './tracker';

// ============================================================================
// Hook: useUserIdentity
// ============================================================================

/**
 * 사용자 로그인/로그아웃 시 analytics 식별 처리
 *
 * @param user - Supabase auth user (null이면 로그아웃 상태)
 */
export function useUserIdentity(user: User | null): void {
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const tracker = getAnalyticsTracker();

    if (!user) {
      // 로그아웃: 상태 초기화
      if (identifiedUserIdRef.current) {
        identifiedUserIdRef.current = null;
        tracker.clearUser();
      }
      return;
    }

    // 이미 식별된 사용자면 스킵
    if (user.id === identifiedUserIdRef.current) {
      return;
    }

    // 🎯 email 우선 정책
    const distinctId = user.email || user.id;

    // 🎯 Mixpanel identify 호출 (SDK가 자동으로 anonymous → identified ID merge)
    Mixpanel.identify(distinctId);

    // 🎯 사용자 프로필 설정 (이벤트 트래킹은 로그인 액션 발생 시점에서)
    setUserProfile(user);

    // 식별 완료 표시
    identifiedUserIdRef.current = user.id;
  }, [user]);
}

// ============================================================================
// Helper: setUserProfile
// ============================================================================

/**
 * 사용자 프로필 설정 (이벤트 트래킹 없음)
 *
 * 🎯 이벤트는 실제 로그인/회원가입 액션이 발생한 곳에서 트래킹
 * - 이메일 로그인: EmailLoginForm.tsx
 * - Google 로그인: TrackedAuthService.ts
 * - 회원가입: TrackedAuthService.ts
 *
 * @param user - Supabase auth user
 */
function setUserProfile(user: User): void {
  // 🎯 email 우선 정책
  const distinctId = user.email || user.id;

  const userProperties = {
    $email: user.email,
    $name: user.user_metadata?.username || '',
    created_at: user.created_at,
  };

  // 🎯 모든 트래커에 사용자 정보 설정
  const tracker = getAnalyticsTracker();
  tracker.setUser(distinctId, userProperties);
}
